import { Divider, Group, SimpleGrid, Stack, createStyles, useMantineTheme } from '@mantine/core';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';

import { reportEvent } from '~/domains/analytics';
import { ButtonLink } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import { EdgeBoostIcon } from '~/domains/payments/icons/EdgeBoostIcon';
import dt from '~/testing';

import { EDGE_BOOST_DISPLAY_URL, EDGE_BOOST_URL } from '../../constants';

import { BackButton } from './BackButton';
import { NumberedRow } from './NumberedRow';

const HIGHLIGHT_KEYS = ['instant', 'limit'] as const;
const STEP_KEYS = ['one', 'two', 'three'] as const;

const useStyles = createStyles((theme) => ({
  highlight: {
    border: `1px solid ${theme.colors.gray2[0]}`,
    borderRadius: theme.radius.md,
    padding: theme.other.spacing._12,
  },
}));

type EdgeBoostHighlightProps = {
  highlightKey: (typeof HIGHLIGHT_KEYS)[number];
};

function EdgeBoostHighlight({ highlightKey }: EdgeBoostHighlightProps) {
  const { t } = useTranslation('payments');
  const { classes, theme } = useStyles();

  return (
    <Stack spacing={theme.other.spacing._4} className={classes.highlight}>
      <Text variant="subhead-small">
        {t(`deposits.edgeBoost.highlights.${highlightKey}.title`)}
      </Text>
      <Text variant="body-small" c="gray2.6">
        {t(`deposits.edgeBoost.highlights.${highlightKey}.description`)}
      </Text>
    </Stack>
  );
}

type EdgeBoostStepProps = {
  stepKey: (typeof STEP_KEYS)[number];
  index: number;
};

function EdgeBoostStep({ stepKey, index }: EdgeBoostStepProps) {
  return (
    <NumberedRow number={index + 1}>
      <Trans
        i18nKey={`payments:deposits.edgeBoost.steps.${stepKey}`}
        components={{ bold: <strong /> }}
      />
    </NumberedRow>
  );
}

type EdgeBoostIntroSectionProps = {
  onBack: () => void;
};

export function EdgeBoostIntroSection({ onBack }: EdgeBoostIntroSectionProps) {
  const { t } = useTranslation('payments');
  const theme = useMantineTheme();

  return (
    <Stack
      spacing={theme.other.spacing._32}
      data-test-id={dt.payments.deposits.edgeBoostIntroSection.container}
    >
      <div>
        <BackButton
          onClick={onBack}
          data-test-id={dt.payments.deposits.edgeBoostIntroSection.backButton}
        />
        <Stack spacing={theme.other.spacing._8}>
          <Group spacing={theme.other.spacing._8} noWrap>
            <Title variant="display-headline-expanded-xl" c="gray2.6">
              {t('deposits.edgeBoost.title')}
            </Title>
            <EdgeBoostIcon size={14} />
          </Group>
          <Text variant="body-large" c="gray2.6">
            {t('deposits.edgeBoost.description')}
          </Text>
        </Stack>
      </div>

      <SimpleGrid cols={2} spacing={theme.other.spacing._8}>
        {HIGHLIGHT_KEYS.map((key) => (
          <EdgeBoostHighlight key={key} highlightKey={key} />
        ))}
      </SimpleGrid>

      <Stack spacing={theme.other.spacing._16}>
        <Title variant="subhead-medium">{t('deposits.edgeBoost.howItWorks')}</Title>
        <Stack spacing={theme.other.spacing._12}>
          {STEP_KEYS.map((key, index) => (
            <EdgeBoostStep key={key} stepKey={key} index={index} />
          ))}
        </Stack>
      </Stack>

      <Stack spacing={theme.other.spacing._12}>
        <Divider />
        <Text variant="body-small" c="gray2.6" ta="center">
          {t('deposits.edgeBoost.footerNote', { url: EDGE_BOOST_DISPLAY_URL })}
        </Text>
        <ButtonLink
          href={EDGE_BOOST_URL}
          target="_blank"
          rel="noopener noreferrer"
          variant="primary-fill"
          size="large"
          fullWidth
          data-test-id={dt.payments.deposits.edgeBoostIntroSection.continueButton}
          onClick={() => {
            reportEvent('Transactions > EDGE Boost Continue');
          }}
        >
          {t('deposits.edgeBoost.continue')}
        </ButtonLink>
      </Stack>
    </Stack>
  );
}
